import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { metricsService } from "../../services/metricsService";
import { getMyRoles } from "../../services/userService";
import { getFullName } from "../../services/auth";

function ActivityTracker() {
  const location = useLocation();
  const { accounts } = useMsal();
  const lastUpdateRef = useRef(Date.now());
  const lastActivityRef = useRef(Date.now());
  const activeStartRef = useRef(null);

  useEffect(() => {
    const firstMetricCheck = async () => {
      if (accounts.length > 0) {
        const firstMetric = await metricsService.getMetricList({
          names: ["first_session"],
          includes_tags: ["username:" + accounts[0].username],
          returnRaw: true,
        });
        if (firstMetric.error) {
          console.error(
            "Error fetching first session metric:",
            firstMetric.error,
          );
          return;
        }
        if (firstMetric.data?.length > 0) {
          return;
        }
        const request = {
          name: "first_session",
          value: 1,
          tags: ["username:" + accounts[0].username],
        };
        await metricsService.createMetric(request);
      }
    };

    // Wait a few seconds before checking first session metric
    setTimeout(firstMetricCheck, 5000);

    const updateUserActivity = async () => {
      if (accounts.length > 0) {
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateRef.current;

        if (timeSinceLastUpdate >= 60000) {
          // Calculate active time since last update
          let activeTime = 0;
          if (activeStartRef.current !== null) {
            activeTime = now - activeStartRef.current;
            activeTime = Math.min(activeTime, timeSinceLastUpdate);
          }

          if (activeTime > 0) {
            const request = {
              name: "user_activity",
              value: Math.floor(activeTime / 1000), // Convert to seconds
              tags: ["periodic_update", "username:" + accounts[0].username],
              metadata: [
                { key: "roles", value: (await getMyRoles()).join(",") },
                {
                  key: "recentRoutes",
                  value: localStorage.getItem("recents") || "[]",
                },
                { key: "fullName", value: await getFullName() },
              ],
            };
            await metricsService.createMetric(request);
            lastUpdateRef.current = now;
            // Reset active start time to current time if still active
            if (now - lastActivityRef.current < 300000) {
              activeStartRef.current = now;
            } else {
              activeStartRef.current = null;
            }
          } else {
            // If no activity, just update the last update time
            lastUpdateRef.current = now;
          }
        }
      }
    };

    const detectActivity = () => {
      const now = Date.now();
      if (activeStartRef.current === null) {
        activeStartRef.current = now;
      }
      lastActivityRef.current = now;
    };

    // Detect user activity
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, detectActivity));

    // Check for activity and update backend if necessary
    const interval = setInterval(updateUserActivity, 60000); // Check every minute

    // Initial update - wait a few seconds before first update to allow MSAL to finish
    setTimeout(updateUserActivity, 6000);

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, detectActivity),
      );
      clearInterval(interval);
    };
  }, [location, accounts]);

  return null;
}

export default ActivityTracker;
